import {
  WHLoginDataService,
  WHIconENUM,
  WHFeatureKeyENUM,
  WHWorkObjectTemplateDOM,
} from '@workheld/workheld-shared-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import {
  Component,
  ViewChild,
  ChangeDetectorRef,
  signal,
  inject,
  effect,
  DestroyRef,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { MatTableDataSource } from '@angular/material/table';
import {
  WorkObjectTemplateAsyncService,
  WHUseWorkObjectTemplatePayloadDTO,
} from 'src/app/app-services-async/work-object-template-async.service';
import { NgFormBuilderHelperService } from 'src/app/app-services-helper/ng-form-builder-helper.service';
import { Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ListColumn } from 'src/app/app-modules-layout/list/list-column.model';
import { UseWorkObjectTemplateDialogStore } from './use-work-object-template-dialog.store';

@Component({
  selector: 'ng-bee-mat-dialog-use-work-object-template-entry',
  templateUrl: './mat-dialog-use-work-object-template-entry.component.html',
  styleUrls: ['./mat-dialog-use-work-object-template-entry.component.scss'],
  standalone: false,
  providers: [UseWorkObjectTemplateDialogStore],
})
export class MatDialogUseWorkObjectTemplateEntryComponent {
  // STORES
  protected store = inject(UseWorkObjectTemplateDialogStore);

  // SERVICES
  private readonly workObjectTemplateAsyncService = inject(
    WorkObjectTemplateAsyncService,
  );
  private readonly ngFormBuilderHelperService = inject(
    NgFormBuilderHelperService,
  );
  private readonly loginDataService = inject(WHLoginDataService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  // DIALOG
  public matDialogRef = inject(
    MatDialogRef<MatDialogUseWorkObjectTemplateEntryComponent>,
  );
  public data: { workObjectId: string } = inject(MAT_DIALOG_DATA);

  // FORM
  public applyWorkObjectTemplateFromGroup: FormGroup =
    this.workObjectTemplateAsyncService.initApplyWorkObjectTemplateFromGroup();
  public locationFormGroup: FormGroup =
    this.ngFormBuilderHelperService.initLocationFormGroup(true);

  // DATA
  public isLocationEnabled: boolean = false;
  public selectedWorkObjectTemplateDOM: WHWorkObjectTemplateDOM | null;

  // DATA TABLE
  public dataSource = new MatTableDataSource<WHWorkObjectTemplateDOM>([]);

  // TABLE COLUMS
  public columns: ListColumn[] = [
    { name: '', property: 'id', visible: true },
    {
      name: 'template.model.extid',
      property: 'workObjectTypeId',
      visible: false,
      isModelProperty: true,
    },
    {
      name: 'template.model.title',
      property: 'templateName',
      visible: false,
      isModelProperty: true,
    },
    {
      name: 'template.model.title',
      property: 'formattedName',
      visible: true,
      isModelProperty: true,
    },
  ] as ListColumn[];

  // DOM
  @ViewChild(MatPaginator, { static: true }) public paginator: MatPaginator;
  @ViewChild('workOrderSelectionList', { static: false })
  workOrderSelectionList: MatSelectionList;

  // STATE
  public saving: boolean = false;
  selectedStep = signal(0);

  // MANAGE SUBSCRIPTIONS
  public subscriptions: Subscription[] = [];

  constructor() {
    this.applyWorkObjectTemplateFromGroup.controls['workObjectId'].patchValue(
      this.data.workObjectId,
    );

    const featureSub = this.loginDataService.featureConfigMap$.subscribe(
      (featureConfigMap: Map<string, boolean>) => {
        this.isLocationEnabled = featureConfigMap.get(
          WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_ORDER_LOCATION,
        );
      },
    );
    this.subscriptions.push(featureSub);

    effect(() => {
      this.dataSource.data = this.store.templates();
    });

    effect(() => {
      if (!this.store.isLoadingWorkOrders() && this.store.workOrderTemplates().length > 0) {
        setTimeout(() => this.workOrderSelectionList?.selectAll(), 0);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.subscriptions.forEach((subscription: Subscription) => {
        subscription.unsubscribe();
      });
    });
  }

  public search(searchTerm: string): void {
    this.store.setSearchTerm(searchTerm);
    this.paginator.firstPage();
  }

  public onPageChange(event: PageEvent): void {
    if (event.pageSize !== this.store.filters().pageSize) {
      this.store.setPageSize(event.pageSize);
      this.paginator.firstPage();
    } else {
      this.store.setPage(event.pageIndex);
    }
  }

  public selectWorkObjectTemplate(
    workObjectTemplateDOM: WHWorkObjectTemplateDOM,
  ) {
    if (
      this.selectedWorkObjectTemplateDOM &&
      this.selectedWorkObjectTemplateDOM.id === workObjectTemplateDOM.id
    ) {
      this.selectedWorkObjectTemplateDOM = null;
      this.store.clearWorkOrderTemplates();
      this.applyWorkObjectTemplateFromGroup.reset();
      return;
    }

    this.selectedWorkObjectTemplateDOM = workObjectTemplateDOM;
    this.applyWorkObjectTemplateFromGroup.controls['workObjectId'].patchValue(
      this.data.workObjectId,
    );
    this.applyWorkObjectTemplateFromGroup.controls[
      'workObjectTemplateId'
    ].patchValue(workObjectTemplateDOM.id);
    this.store.loadWorkOrderTemplates(workObjectTemplateDOM.id);
  }

  public handleSubmit() {
    // PATCH PAYLOAD
    if (this.isLocationEnabled) {
      this.applyWorkObjectTemplateFromGroup.controls['address'].patchValue(
        this.locationFormGroup.value,
      );
    } else {
      this.applyWorkObjectTemplateFromGroup.controls['address'].disable();
    }
    const selectedWorkOrderTemplates: any[] = [];
    this.workOrderSelectionList._value.forEach(
      (workOrderTemplateId: string) => {
        selectedWorkOrderTemplates.push({
          workOrderTemplateId,
        });
      },
    );
    this.applyWorkObjectTemplateFromGroup.controls[
      'selectedWorkOrderTemplates'
    ].patchValue(selectedWorkOrderTemplates);
    const workObjectTemplatePayload: WHUseWorkObjectTemplatePayloadDTO =
      this.applyWorkObjectTemplateFromGroup.value;

    // TEMPLATE ID
    const templateId: string =
      this.applyWorkObjectTemplateFromGroup.controls['workObjectTemplateId']
        .value;

    this.saving = true;
    this.subscriptions.push(
      this.workObjectTemplateAsyncService
        .useWorkObjectTemplate(templateId, workObjectTemplatePayload)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.saving = false;
            this.changeDetectorRef.detectChanges();
            return throwError(() => error);
          }),
        )
        .subscribe((resp: any) => {
          this.saving = false;
          this.matDialogRef.close(true);
        }),
    );
  }

  public cancel() {
    this.matDialogRef.close();
  }

  public get visibleColumns() {
    return this.columns
      .filter((column) => column.visible)
      .map((column) => column.property);
  }

  public get isWorkObjectTemplateSelected(): string {
    return this.applyWorkObjectTemplateFromGroup.controls[
      'workObjectTemplateId'
    ].value as string;
  }

  public get isLocationValid() {
    return this.locationFormGroup.controls['formattedAddress'].valid;
  }

  public get projectTemplateIcon(): string {
    return WHIconENUM.ProjectTemplateIcon as string;
  }

  public get saveIcon(): string {
    return WHIconENUM.SaveIcon as string;
  }

  public get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }
}
